import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import Grid from "@/config/Grid/Grid";
import Button from "@/components/Button/Button";
import IconButton from "@/components/IconButton/IconButton";
import PaymentMethodPicker from "@/components/PaymentMethodPicker/PaymentMethodPicker";
import TransparentButton from "@/components/TransparentButton/TransparentButton";
import TransparentTextField from "@/components/TransparentTextField/TransparentTextField";
import TransparentTile from "@/components/TransparentTile/TransparentTile";
import WaiterAvatar from "@/components/WaiterAvatar/WaiterAvatar";
import WaiterDrawer from "@/components/WaiterDrawer/WaiterDrawer";
import OrderActionBar from "@/layouts/Order/components/OrderActionBar";
import { payTip } from "@/api/Tips/TipsAPI";
import type { Waiter } from "@/api/Waiters/WaitersAPI.model";
import { useWaiters } from "@/hooks/useWaiters";
import { useTipFeeBps } from "@/store/useVenuesDataStore";
import { formatGrosze, tipFeeGrosze } from "@/helpers/cartPricing";
import { useGoBack } from "@/hooks/useGoBack";
import { tablePath } from "@/routes/paths";
import styles from "./Tip.module.scss";

/** Ready-made amounts, in grosze. */
const TIP_AMOUNTS = [1000, 2000, 5000];

const CUSTOM = "custom";

/**
 * Tipping on its own, away from any bill.
 *
 * The bill flow already lets a guest add a tip to what they are paying. This
 * is the other case: someone who wants to thank one person, whether or not
 * there is anything to settle. It therefore asks the same three questions in
 * one screen, rather than as steps of a checkout.
 */
function TipPage() {
  const { restaurantId = "", tableId = "" } = useParams<{
    restaurantId: string;
    tableId: string;
  }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goBack = useGoBack(tablePath(restaurantId, tableId));

  const [selectedAmount, setSelectedAmount] = useState<string>("");
  const [customAmount, setCustomAmount] = useState("");
  const waiters = useWaiters(restaurantId);
  const [waiter, setWaiter] = useState<Waiter | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  /** More than one person on the floor is a choice; one is not. */
  const hasChoice = waiters.length > 1;

  /**
   * The picker opens itself once, as soon as there is a choice to make. It is
   * the first question this screen asks, and the guest arrived by pressing
   * "leave a tip", so there is nothing else to do here first.
   *
   * A venue with a single waiter skips it: the tip is theirs either way, and a
   * sheet with one tile in it asks the guest to confirm the obvious.
   */
  const prompted = useRef(false);
  useEffect(() => {
    if (prompted.current || waiters.length === 0) return;
    prompted.current = true;

    if (waiters.length === 1) {
      setWaiter(waiters[0]);
      return;
    }
    setPickerOpen(true);
  }, [waiters]);
  const [paymentMethod, setPaymentMethod] = useState<string | null>(null);
  const [blikCode, setBlikCode] = useState("");
  const [status, setStatus] = useState<"idle" | "paying" | "done" | "error">(
    "idle",
  );

  const isCustom = selectedAmount === CUSTOM;

  const amountGrosze = isCustom
    ? Math.round((parseFloat(customAmount.replace(",", ".")) || 0) * 100)
    : Number(selectedAmount || 0);

  /**
   * The person named receives `amountGrosze`; the venue's fee is charged on
   * top, so the total is what leaves the guest's card. The backend applies the
   * same rule to the same rate, so these agree to the grosz.
   */
  const tipFeeBps = useTipFeeBps();
  const feeGrosze = tipFeeGrosze(amountGrosze, tipFeeBps);
  const totalGrosze = amountGrosze + feeGrosze;

  const blikReady = paymentMethod !== "Blik" || blikCode.length === 6;
  const canPay =
    amountGrosze > 0 && Boolean(paymentMethod) && blikReady && status !== "paying";

  const handlePay = async () => {
    if (!canPay) return;
    setStatus("paying");
    try {
      await payTip(restaurantId, {
        tableId,
        amountGrosze,
        userToTip: waiter?.userId,
        paymentMethodName: paymentMethod,
      });
      setStatus("done");
    } catch {
      setStatus("error");
    }
  };

  if (status === "done" || status === "error") {
    const failed = status === "error";
    return (
      <Grid>
        <div className={`start-1 col-4 ${styles.tipWrapper}`}>
          <div className={styles.tipTerminalContent}>
            <i
              className={`las ${
                failed ? "la-times-circle" : "la-check-circle"
              } ${styles.tipTerminalIcon}`}
            />
            <p className={`headline-small ${styles.tipTerminalTitle}`}>
              {failed ? t("tip.failed.title") : t("tip.confirmed.title")}
            </p>
            <p className={`body-medium ${styles.tipTerminalDescription}`}>
              {failed
                ? t("tip.failed.description")
                : t("tip.confirmed.description")}
            </p>

            <div className={styles.tipTerminalActions}>
              {failed ? (
                <Button
                  onClick={() => setStatus("idle")}
                  label={t("order.button.tryAgain")}
                  variant="contained"
                  color="primary"
                  maxWidth
                  mobile={true}
                />
              ) : null}
              <Button
                onClick={() =>
                  navigate(tablePath(restaurantId, tableId), { replace: true })
                }
                label={t("tip.button.back")}
                variant={failed ? "outlined" : "contained"}
                color="primary"
                maxWidth
                mobile={true}
              />
            </div>
          </div>
        </div>
      </Grid>
    );
  }

  return (
    <>
      <Grid>
        <div className={`start-1 col-4 ${styles.tipWrapper}`}>
          <div className={styles.tipHeaderContainer}>
            <div className={styles.tipHeader}>
              <IconButton
                iconName="las la-angle-left icon-big"
                onClick={goBack}
                color="primary"
                variant="text"
              />
              <p className={`body-bold-large ${styles.tipTitle}`}>
                {t("tip.title")}
              </p>
              <IconButton
                iconName=""
                onClick={() => {}}
                color="primary"
                variant="text"
              />
            </div>
          </div>

          <div className={styles.tipContent}>
            {waiters.length > 0 ? (
              <div
                className={styles.tipWaiter}
                onClick={hasChoice ? () => setPickerOpen(true) : undefined}
                role={hasChoice ? "button" : undefined}
                tabIndex={hasChoice ? 0 : undefined}
                onKeyDown={
                  hasChoice
                    ? (event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setPickerOpen(true);
                        }
                      }
                    : undefined
                }
              >
                {waiter ? (
                  <>
                    <WaiterAvatar
                      name={waiter.name}
                      photoUrl={waiter.photoUrl}
                      size="large"
                    />
                    <p className={`title-large ${styles.tipWaiterName}`}>
                      {waiter.name}
                    </p>
                  </>
                ) : (
                  <p className={`body-bold-medium ${styles.tipWaiterName}`}>
                    {t("tip.waiter.wholeTeam")}
                  </p>
                )}
                {hasChoice ? (
                  <p className={`body-small ${styles.tipWaiterChange}`}>
                    {t("tip.waiter.change")}
                  </p>
                ) : null}
              </div>
            ) : null}

            <TransparentTile>
              <div className={styles.tipAmountTile}>
                <p className={`body-medium ${styles.tipAmountLabel}`}>
                  {t("gradientDrawer.leaveTip.tipAmount")}
                </p>
                {isCustom ? (
                  <TransparentTextField
                    value={customAmount}
                    setValue={setCustomAmount}
                  />
                ) : (
                  <p className={`headline-medium ${styles.tipAmountValue}`}>
                    {formatGrosze(amountGrosze)}
                  </p>
                )}
              </div>
            </TransparentTile>

            <div className={styles.tipAmountOptions}>
              {TIP_AMOUNTS.map((amount) => (
                <TransparentButton
                  key={amount}
                  text={formatGrosze(amount)}
                  value={`${amount}`}
                  selectedValue={selectedAmount}
                  setValue={() => setSelectedAmount(`${amount}`)}
                />
              ))}
            </div>
            <TransparentButton
              text={t("gradientDrawer.leaveTip.customTip")}
              value={CUSTOM}
              selectedValue={selectedAmount}
              setValue={() => setSelectedAmount(CUSTOM)}
            />

            {amountGrosze > 0 ? (
              <div className={styles.tipSummary}>
                <div className={styles.tipSummaryRow}>
                  <p className={`body-medium ${styles.tipSummaryLabel}`}>
                    {waiter
                      ? t("tip.summary.recipient", { name: waiter.name })
                      : t("tip.summary.venue")}
                  </p>
                  <p className={`body-medium ${styles.tipSummaryValue}`}>
                    {formatGrosze(amountGrosze)}
                  </p>
                </div>
                {feeGrosze > 0 ? (
                  <div className={styles.tipSummaryRow}>
                    <p className={`body-medium ${styles.tipSummaryLabel}`}>
                      {t("order.payment.checkoutFee")}
                    </p>
                    <p className={`body-medium ${styles.tipSummaryValue}`}>
                      {formatGrosze(feeGrosze)}
                    </p>
                  </div>
                ) : null}
                <div className={styles.tipSummaryRow}>
                  <p className={`body-bold-medium ${styles.tipSummaryLabel}`}>
                    {t("tip.summary.total")}
                  </p>
                  <p className={`body-bold-medium ${styles.tipSummaryValue}`}>
                    {formatGrosze(totalGrosze)}
                  </p>
                </div>
              </div>
            ) : null}

            <p className={`body-medium ${styles.tipSectionLabel}`}>
              {t("order.payment.payWith")}
            </p>
            <PaymentMethodPicker
              selected={paymentMethod}
              onSelect={setPaymentMethod}
              blikCode={blikCode}
              setBlikCode={setBlikCode}
            />
          </div>
        </div>
      </Grid>

      <WaiterDrawer
        open={pickerOpen}
        waiters={waiters}
        onSelect={(chosen) => {
          setWaiter(chosen);
          setPickerOpen(false);
        }}
        onClose={() => setPickerOpen(false)}
      />

      <OrderActionBar
        summaryLabel={t("tip.summary.total")}
        summaryValue={formatGrosze(totalGrosze)}
      >
        <Button
          onClick={handlePay}
          label={
            status === "paying"
              ? t("order.button.paying")
              : t("order.button.pay")
          }
          variant="contained"
          color="primary"
          maxWidth
          mobile={true}
          disabled={!canPay}
        />
      </OrderActionBar>
    </>
  );
}

export default TipPage;
